import { useState, useEffect, useCallback } from 'react'

export interface AnalyticsData {
  siteVisits: number
  blogViews: number
  blogReads: number
  profileDownloads: number
  blogReadsBySlug: Record<string, number>
  visitsByDate: Record<string, number>
  trafficSources: Record<string, number>
}

const STORAGE_KEY = 'belo_analytics'
const SESSION_KEY = 'belo_analytics_session'
const ANALYTICS_UPDATED_EVENT = 'belo_analytics_updated'

const defaultAnalytics: AnalyticsData = {
  siteVisits: 0,
  blogViews: 0,
  blogReads: 0,
  profileDownloads: 0,
  blogReadsBySlug: {},
  visitsByDate: {},
  trafficSources: {},
}

function getStoredAnalytics(): AnalyticsData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...defaultAnalytics }
    return { ...defaultAnalytics, ...JSON.parse(raw) }
  } catch {
    return { ...defaultAnalytics }
  }
}

function saveAnalytics(data: AnalyticsData) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  window.dispatchEvent(new Event(ANALYTICS_UPDATED_EVENT))
}

function getTodayKey(): string {
  return new Date().toISOString().split('T')[0]
}

// Detect traffic source from referrer and URL params
function detectTrafficSource(): string {
  // Check URL params first (utm_source, ref)
  try {
    const params = new URLSearchParams(window.location.search)
    const utmSource = params.get('utm_source')?.toLowerCase()
    const ref = params.get('ref')?.toLowerCase()
    const source = utmSource || ref

    if (source) {
      if (source.includes('google')) return 'Google Search'
      if (source.includes('chatgpt') || source.includes('openai'))
        return 'ChatGPT'
      if (source.includes('gemini')) return 'Gemini'
      if (source.includes('claude')) return 'Claude'
      if (source.includes('facebook') || source.includes('fb'))
        return 'Facebook'
      if (source.includes('instagram') || source.includes('ig'))
        return 'Instagram'
      if (source.includes('twitter') || source.includes('x.com'))
        return 'X (Twitter)'
      if (source.includes('whatsapp') || source.includes('wa'))
        return 'WhatsApp'
      if (source.includes('linkedin')) return 'LinkedIn'
      if (source.includes('tiktok')) return 'TikTok'
      // Return the raw source if no match
      return source.charAt(0).toUpperCase() + source.slice(1)
    }
  } catch {
    // URL parsing failed, continue to referrer check
  }

  // Check document.referrer
  const referrer = document.referrer.toLowerCase()

  if (!referrer) return 'Direct'

  // Search engines
  if (
    referrer.includes('google.com') &&
    !referrer.includes('gemini.google.com')
  )
    return 'Google Search'
  if (referrer.includes('bing.com')) return 'Bing'
  if (referrer.includes('yahoo.com')) return 'Yahoo'
  if (referrer.includes('duckduckgo.com')) return 'DuckDuckGo'

  // AI assistants
  if (referrer.includes('chat.openai.com') || referrer.includes('chatgpt.com'))
    return 'ChatGPT'
  if (referrer.includes('gemini.google.com')) return 'Gemini'
  if (referrer.includes('claude.ai')) return 'Claude'
  if (referrer.includes('perplexity.ai')) return 'Perplexity'
  if (referrer.includes('copilot.microsoft.com')) return 'Copilot'

  // Social media
  if (
    referrer.includes('facebook.com') ||
    referrer.includes('fb.com') ||
    referrer.includes('fbcdn.net')
  )
    return 'Facebook'
  if (referrer.includes('instagram.com')) return 'Instagram'
  if (
    referrer.includes('twitter.com') ||
    referrer.includes('t.co') ||
    referrer.includes('x.com')
  )
    return 'X (Twitter)'
  if (referrer.includes('whatsapp.com') || referrer.includes('wa.me'))
    return 'WhatsApp'
  if (referrer.includes('linkedin.com') || referrer.includes('lnkd.in'))
    return 'LinkedIn'
  if (referrer.includes('tiktok.com')) return 'TikTok'
  if (referrer.includes('youtube.com') || referrer.includes('youtu.be'))
    return 'YouTube'
  if (referrer.includes('reddit.com')) return 'Reddit'
  if (referrer.includes('pinterest.com')) return 'Pinterest'

  // If referrer is from the same domain, it's internal navigation (treat as Direct)
  try {
    const referrerHost = new URL(referrer).hostname
    const currentHost = window.location.hostname
    if (referrerHost === currentHost) return 'Direct'
  } catch {
    // URL parsing failed
  }

  // Unknown external referrer — extract domain name
  try {
    const host = new URL(referrer).hostname.replace('www.', '')
    return host.charAt(0).toUpperCase() + host.slice(1)
  } catch {
    return 'Other'
  }
}

// Track a unique site visit (once per session)
export function trackSiteVisit(): void {
  const sessionVisited = sessionStorage.getItem(SESSION_KEY)
  if (sessionVisited) return // Already counted this session

  sessionStorage.setItem(SESSION_KEY, 'true')
  const data = getStoredAnalytics()
  data.siteVisits += 1

  const today = getTodayKey()
  data.visitsByDate[today] = (data.visitsByDate[today] || 0) + 1

  // Track traffic source
  const source = detectTrafficSource()
  if (!data.trafficSources) data.trafficSources = {}
  data.trafficSources[source] = (data.trafficSources[source] || 0) + 1

  saveAnalytics(data)
}

// Track blog listing page view
export function trackBlogView(): void {
  const data = getStoredAnalytics()
  data.blogViews += 1
  saveAnalytics(data)
}

// Track individual blog post read
export function trackBlogRead(slug: string): void {
  const data = getStoredAnalytics()
  data.blogReads += 1
  data.blogReadsBySlug[slug] = (data.blogReadsBySlug[slug] || 0) + 1
  saveAnalytics(data)
}

// Track company profile download
export function trackProfileDownload(): void {
  const data = getStoredAnalytics()
  data.profileDownloads += 1
  saveAnalytics(data)
}

// Reset all analytics
export function resetAnalytics(): void {
  localStorage.removeItem(STORAGE_KEY)
  sessionStorage.removeItem(SESSION_KEY)
  window.dispatchEvent(new Event(ANALYTICS_UPDATED_EVENT))
}

// Hook for reading analytics (reactive)
export function useAnalytics() {
  const [analytics, setAnalytics] = useState<AnalyticsData>(getStoredAnalytics)

  const refetch = useCallback(() => {
    setAnalytics(getStoredAnalytics())
  }, [])

  useEffect(() => {
    const handler = () => setAnalytics(getStoredAnalytics())
    window.addEventListener(ANALYTICS_UPDATED_EVENT, handler)
    return () => window.removeEventListener(ANALYTICS_UPDATED_EVENT, handler)
  }, [])

  // Get recent 7 days of visits
  const recentVisits = (() => {
    const days: { date: string; count: number }[] = []
    for (let i = 6; i >= 0; i--) {
      const d = new Date()
      d.setDate(d.getDate() - i)
      const key = d.toISOString().split('T')[0]
      days.push({
        date: d.toLocaleDateString('en-US', { weekday: 'short' }),
        count: analytics.visitsByDate[key] || 0,
      })
    }
    return days
  })()

  // Top blog posts by reads
  const topBlogPosts = Object.entries(analytics.blogReadsBySlug)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([slug, count]) => ({ slug, count }))

  // Traffic sources sorted by count
  const trafficSources = Object.entries(analytics.trafficSources || {})
    .sort(([, a], [, b]) => b - a)
    .map(([source, count]) => ({ source, count }))

  return {
    analytics,
    recentVisits,
    topBlogPosts,
    trafficSources,
    refetch,
  }
}
